import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserProvider } from "@/contexts/UserContext";
import { RefreshProvider } from "@/contexts/RefreshContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Index from "./pages/Index";
import CheckIn from "./pages/CheckIn";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Team from "./pages/Team";
import TeamTransition from "./pages/TeamTransition";
import NotFound from "./pages/NotFound";
import LoginPage from "@/components/auth/LoginPage";
import Chat from "@/pages/Chat";
import InvitePage from "@/pages/InvitePage";
import Pricing from "./pages/Pricing";
import PaymentReturn from "./pages/PaymentReturn";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import CalendarCallback from "./pages/CalendarCallback";
import SlackCallback from "./pages/SlackCallback";
import PatternAnalysisDashboard from "./components/PatternAnalysisDashboard";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageErrorBoundary } from "./components/PageErrorBoundary";
import FullOneOnOnePrepPage from "./components/manager/FullOneOnOnePrepPage";
import DevPage from "./pages/DevPage";
import { toast } from "sonner";

// Initialize Tanstack Query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Main app content
function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  return (
    <ErrorBoundary>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Routes>
          {/* Main route - like web_old: Index when signed in, LoginPage when signed out */}
          <Route 
            path="/" 
            element={
              loading ? (
                <div className="flex items-center justify-center min-h-screen">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : isAuthenticated ? (
                <PageErrorBoundary>
                  <Index />
                </PageErrorBoundary>
              ) : (
                <LoginPage />
              )
            } 
          />
          
          {/* Public routes */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/payment-return" element={<PaymentReturn />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/calendar/callback" element={<CalendarCallback />} />
          <Route path="/slack/callback" element={<SlackCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/devpage" element={<DevPage />} />

          {/* Legacy /dashboard route - redirect to / */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route
            path="/check-in"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <CheckIn />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Legacy /checkin route - redirect to /check-in */}
          <Route path="/checkin" element={<Navigate to="/check-in" replace />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <Profile />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <Settings />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <Team />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-transition"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <TeamTransition />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <Chat />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:chatId"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <Chat />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invite/:inviteCode"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <InvitePage />
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patterns"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <PatternAnalysisDashboard />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/one-on-one"
            element={
              <ProtectedRoute>
                <PageErrorBoundary>
                  <Layout>
                    <FullOneOnOnePrepPage />
                  </Layout>
                </PageErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Legacy /meetings/prep route - redirect to /one-on-one */}
          <Route path="/meetings/prep" element={<Navigate to="/one-on-one" replace />} />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </TooltipProvider>
    </ErrorBoundary>
  );
}

// Main App component
const App = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <UserProvider>
              <RefreshProvider>
                <AppContent />
              </RefreshProvider>
            </UserProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
